import { useState } from 'react';
import { Panel, Button, Input, ConfirmModal, Skeleton, EmptyState } from '@/components/ui';
import {
  useAgencySpreads,
  useUpdateAgencySpread,
  useRevertAgencySpread,
  type AgencySpreadRow,
  type AgencyRateType,
} from '@/hooks/use-agency-spreads';

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function AgencySpreads() {
  const { data: rows, isLoading, isError, error, refetch } = useAgencySpreads();
  const update = useUpdateAgencySpread();
  const revert = useRevertAgencySpread();

  const [editing, setEditing] = useState<AgencyRateType | null>(null);
  const [spreadInput, setSpreadInput] = useState<string>('');
  const [noteInput, setNoteInput] = useState<string>('');
  const [validationMsg, setValidationMsg] = useState<string | null>(null);
  const [confirmRevert, setConfirmRevert] = useState<AgencySpreadRow | null>(null);

  if (isLoading) {
    return (
      <Panel title="Market Spreads">
        <div className="flex flex-col gap-2 p-4">
          <Skeleton variant="block" />
          <Skeleton variant="block" />
        </div>
      </Panel>
    );
  }

  if (isError) {
    return (
      <Panel title="Market Spreads">
        <EmptyState
          title="Failed to load spreads"
          description={error instanceof Error ? error.message : 'Unknown error'}
          action={<Button onClick={() => { refetch(); }}>Retry</Button>}
        />
      </Panel>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <Panel title="Market Spreads">
        <EmptyState title="No spreads configured" description="Spread rows haven't been seeded." />
      </Panel>
    );
  }

  const beginEdit = (row: AgencySpreadRow) => {
    setEditing(row.rate_type);
    setSpreadInput(String(Math.round(row.spread_bps)));
    setNoteInput('');
    setValidationMsg(null);
  };

  const cancelEdit = () => {
    setEditing(null);
    setSpreadInput('');
    setNoteInput('');
    setValidationMsg(null);
  };

  const saveEdit = async (rateType: AgencyRateType) => {
    const parsed = Number(spreadInput);
    if (!Number.isFinite(parsed) || parsed < 50 || parsed > 400) {
      setValidationMsg('Spread must be between 50 and 400 bps');
      return;
    }
    try {
      await update.mutateAsync({
        rateType,
        spread_bps: parsed,
        note: noteInput.trim() || undefined,
      });
      cancelEdit();
    } catch (e) {
      setValidationMsg(e instanceof Error ? e.message : 'Update failed');
    }
  };

  return (
    <Panel title="Market Spreads">
      <div className="flex flex-col gap-3 p-4">
        <p className="text-xs font-mono text-text-muted">
          Admin override for agency MF spreads (Fannie / Freddie). Reverting returns control to the weekly cron.
        </p>
        {rows.map((row) => {
          const isEditing = editing === row.rate_type;
          const isManual = row.source === 'manual';
          return (
            <div
              key={row.rate_type}
              className="flex flex-col gap-2 p-3 border border-panel-border rounded-[3px]"
              data-testid={`spread-row-${row.rate_type}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-body text-text-bright">{row.display_name}</div>
                  <div className="text-xs font-mono text-text-muted">
                    {isManual ? 'manual override' : 'auto'} · updated {formatTimestamp(row.updated_at)}
                  </div>
                </div>
                <div className="text-lg font-mono tabular-nums text-text-bright">
                  {Math.round(row.spread_bps)}bps
                </div>
              </div>

              {row.confidence_note && (
                <div className="text-xs font-mono text-text-muted">{row.confidence_note}</div>
              )}

              {isEditing ? (
                <div className="flex flex-col gap-2">
                  <Input
                    type="number"
                    min={50}
                    max={400}
                    value={spreadInput}
                    onChange={(e) => setSpreadInput(e.target.value)}
                    aria-label={`Spread bps for ${row.display_name}`}
                    suffix="bps (50–400)"
                  />
                  <Input
                    type="text"
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    placeholder="Note (optional, max 500 chars)"
                    aria-label={`Note for ${row.display_name}`}
                    maxLength={500}
                  />
                  {validationMsg && (
                    <div className="text-xs font-mono text-danger" data-testid={`spread-${row.rate_type}-error`}>
                      {validationMsg}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      onClick={() => { void saveEdit(row.rate_type); }}
                      disabled={update.isPending}
                    >
                      {update.isPending ? 'Saving…' : 'Save override'}
                    </Button>
                    <Button variant="secondary" onClick={cancelEdit} disabled={update.isPending}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button variant="primary" onClick={() => beginEdit(row)}>Override</Button>
                  {isManual && (
                    <Button variant="secondary" onClick={() => setConfirmRevert(row)}>
                      Revert to auto
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmModal
        open={confirmRevert !== null}
        title="Revert to auto"
        confirmLabel="Revert"
        onClose={() => setConfirmRevert(null)}
        onConfirm={async () => {
          if (!confirmRevert) return;
          try {
            await revert.mutateAsync({ rateType: confirmRevert.rate_type });
          } finally {
            setConfirmRevert(null);
          }
        }}
      >
        Revert {confirmRevert?.display_name ?? ''} to auto control? The weekly cron will set the value on its next run.
      </ConfirmModal>
    </Panel>
  );
}
